/**
 * rtt.ts — the production round-trip sampler (combat wave, 2026-09-04).
 *
 * The only latency number in the repo was a p99 acknowledgement lag measured
 * over LOOPBACK on a software-rendering box running the full sim — a server
 * workload tail, not the network. Any reaction-window mechanic needs the real
 * figure, so the client times a no-op `ping` reducer every few seconds and
 * keeps a rolling window. `__rtt()` reports it; the perf HUD may show it.
 */
const WINDOW = 60;
const samples: number[] = [];
let nonce = 0;
let timer: number | null = null;
let last = -1;

export interface RttStats { n: number; last: number; median: number; p95: number; p99: number; min: number; max: number }

function pct(sorted: number[], p: number): number {
  if (sorted.length === 0) return -1;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * p)));
  return sorted[i];
}

export function rttStats(): RttStats {
  const s = [...samples].sort((a, b) => a - b);
  return {
    n: s.length, last: Math.round(last * 10) / 10,
    median: Math.round(pct(s, 0.5) * 10) / 10, p95: Math.round(pct(s, 0.95) * 10) / 10, p99: Math.round(pct(s, 0.99) * 10) / 10,
    min: s.length ? Math.round(s[0] * 10) / 10 : -1, max: s.length ? Math.round(s[s.length - 1] * 10) / 10 : -1,
  };
}

export function recordRtt(ms: number): void {
  last = ms;
  samples.push(ms);
  if (samples.length > WINDOW) samples.shift();
}

/** Start sampling against a connection whose reducers carry `ping`. Safe to call twice. */
export function startRttSampler(pingFn: (() => Promise<void>) | null, everyMs = 5000): void {
  if (timer !== null || !pingFn) return;
  const tick = () => {
    const t0 = performance.now();
    pingFn().then(() => recordRtt(performance.now() - t0)).catch(() => { /* a refused ping is not a sample */ });
  };
  timer = window.setInterval(tick, everyMs);
  tick();
  nonce = 0;
  (window as unknown as Record<string, unknown>).__rtt = rttStats;
}

export function stopRttSampler(): void {
  if (timer !== null) { window.clearInterval(timer); timer = null; }
}

export function nextNonce(): number { nonce = (nonce + 1) >>> 0; return nonce; }
